// Extracted from pages/Articles/CreateArticle.js (behavior-preserving move).
// Functions are recreated on every render with the latest wizard state, exactly as
// they were when they lived inside the page component. `log` is the original no-op.
import { flushSync } from 'react-dom';
import { directusAPI } from '../../../api';
import { parseMarkdownToEditorJS } from '../lib/markdownToEditorJS';
import {
  generateArticleViaAPI,
  getStoredToken,
  isApiServerAvailable,
  log,
  markArticleFailed,
} from '../services/articleGenerationApi';

/**
 * Final article generation from the reviewed draft (step 5): API when healthy,
 * otherwise the Directus queue. Runs in the background and returns to the list.
 */
export function useFinalArticleGeneration(ctx) {
  const {
    t,
    navigate,
    queueAPI,
    editorRef,
    loading,
    isGenerationCompleted,
    editorHasContent,
    generatedArticleId,
    articleData,
    sources,
    keywords,
    contentType,
    toneOfVoice,
    complexity,
    language,
    selectedProject,
    selectedStructure,
    includeImages,
    thumbnailSource,
    setSaveError,
    setIsGenerationCompleted,
    setGeneratedArticleId,
    setArticleData,
    setLastSaved,
    setEditorHasContent,
    setShowEditor,
    setIsDraftGenerated,
    setForceEditorRefresh,
  } = ctx;

  /**
   * Updated handleContinue function with API preference
   */
  async function handleContinue() {
    log('\n🔘 ============ HANDLE_CONTINUE START ============');
    log('🔘 HANDLE_CONTINUE: Function called with state:');
    log('  - editorHasContent:', editorHasContent);
    log('  - generatedArticleId:', generatedArticleId);
    log('  - editorRef.current exists:', !!editorRef.current);
    log('  - loading:', loading);
    log('  - isGenerationCompleted:', isGenerationCompleted);

    // Check if editor has any content, if not use a fallback
    let hasContent = editorHasContent;
    if (!hasContent && editorRef.current) {
      try {
        log('🔘 HANDLE_CONTINUE: editorHasContent is false, checking editor manually...');
        const outputData = await editorRef.current.save();
        hasContent = outputData.blocks && outputData.blocks.length > 0;
        log('🔘 HANDLE_CONTINUE: Manual check - editor has', outputData.blocks?.length || 0, 'blocks');
        log('🔘 HANDLE_CONTINUE: First few blocks:', outputData.blocks?.slice(0, 2));
      } catch (error) {
        log('🔘 HANDLE_CONTINUE: Error checking editor content:', error);
      }
    }

    log('🔘 HANDLE_CONTINUE: Final hasContent value:', hasContent);

    if (hasContent) {
      setSaveError('');
      setIsGenerationCompleted(false);

      // Set article status to "ongoing" to prevent concurrent editing
      if (generatedArticleId) {
        try {
          log('🔒 HANDLE_CONTINUE: Setting article status to ongoing');
          await directusAPI.patch(`/items/articles/${generatedArticleId}`, {
            status: 'ongoing',
            queue_status: 'processing'
          }, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getStoredToken()}`
            }
          });
          log('✅ HANDLE_CONTINUE: Article status set to ongoing');
        } catch (error) {
          log('❌ HANDLE_CONTINUE: Error setting ongoing status:', error);
        }
      }

      const runFinalGeneration = async () => {
        try {
          log('\n🚀 ============ WORKERS FINAL ARTICLE GENERATION START ============');

          // Check if API server is available
          const useApi = await isApiServerAvailable();
          const queueAvailable = queueAPI.isQueueAvailable();

          log('🔍 HANDLE_CONTINUE: Workers availability check:');
          log('   - API Server available:', useApi);
          log('   - Queue available:', queueAvailable);

          if (useApi) {
            log('🎯 WORKERS FINAL: ✅ Using API Server for full article');
            await handleContinueViaAPI();
          } else if (queueAvailable) {
            log('🎯 WORKERS FINAL: ✅ Using Queue System for full article');
            await handleContinueWithQueue();
          } else {
            log('❌ WORKERS FINAL: No workers available for final article generation');
            log('❌ WORKERS FINAL: API available:', useApi, 'Queue available:', queueAvailable);
            throw new Error('Workers not available for final article generation. Please ensure workers are running.');
          }

          log('✅ ============ WORKERS FINAL ARTICLE GENERATION END ============\n');
        } catch (error) {
          log('❌ WORKERS FINAL ERROR:', error);
          await markArticleFailed(generatedArticleId);
        }
      };

      // Final generation is server-side. Return to the live status list immediately.
      void runFinalGeneration();
      navigate('/writer/articles');
    } else {
      log('❌ HANDLE_CONTINUE: No content found, cannot proceed with generation');
      log('❌ HANDLE_CONTINUE: editorHasContent was:', editorHasContent);
      setSaveError(t('articles.create.errors.noContent'));
    }
  }

  /**
   * New API-based continue function
   */
  async function handleContinueViaAPI() {
    try {
      log('🚀 API CONTINUE: Starting final article generation from draft');

      // ALWAYS fetch latest draft from Directus to ensure we have the most recent changes
      let outputData;
      let articleId = generatedArticleId;

      if (articleId) {
        try {
          log('📝 API CONTINUE: Fetching latest draft from Directus for article:', articleId);
          const response = await directusAPI.get(`/items/articles/${articleId}`, {
            headers: {
              'Authorization': `Bearer ${getStoredToken()}`
            }
          });

          const latestArticleData = response.data.data;
          if (latestArticleData.article_draft) {
            outputData = typeof latestArticleData.article_draft === 'string'
              ? JSON.parse(latestArticleData.article_draft)
              : latestArticleData.article_draft;
            log('📝 API CONTINUE: ✅ Using LATEST draft from Directus, blocks count:', outputData.blocks?.length || 0);
            log('📝 API CONTINUE: ✅ Draft timestamp:', outputData.time);
          }
        } catch (error) {
          log('📝 API CONTINUE: Error fetching latest draft from Directus:', error);
        }
      }

      // Fallback to editor if Directus fetch failed
      if (!outputData && editorRef.current && editorRef.current.save) {
        try {
          outputData = await editorRef.current.save();
          log('📝 API CONTINUE: Fallback - using editor data, blocks count:', outputData.blocks.length);
        } catch (error) {
          log('📝 API CONTINUE: Error saving from editor:', error);
        }
      }

      // Final fallback to old articleData
      if (!outputData && articleData && articleData.article_draft) {
        try {
          outputData = typeof articleData.article_draft === 'string'
            ? JSON.parse(articleData.article_draft)
            : articleData.article_draft;
          log('📝 API CONTINUE: Final fallback - using old articleData.article_draft, blocks count:', outputData.blocks?.length || 0);
        } catch (error) {
          log('📝 API CONTINUE: Error parsing articleData.article_draft:', error);
          throw new Error('Cannot get article content. Editor is not available and article data is invalid.');
        }
      }

      if (!outputData || !outputData.blocks || outputData.blocks.length === 0) {
        throw new Error('No article content found. Please ensure the draft has content before generating.');
      }

      if (!articleId) {
        throw new Error('No article ID found. Please ensure the draft was saved correctly.');
      }

      // Update article with current draft content
      const updateData = {
        article_draft: JSON.stringify(outputData),
        sources: JSON.stringify(sources),
        keywords: keywords,
        content_type: contentType,
        tone_of_voice: toneOfVoice,
        fog_index: complexity,
        language: language,
        project: selectedProject || null,
        article_linked_structure: selectedStructure || null,
        article_thumbnail_included: includeImages,
        article_thumbnail_source: includeImages ? thumbnailSource : null
      };

      console.log('💾 handleContinueViaAPI: selectedStructure =', selectedStructure, 'updateData:', updateData);

      log('🔄 API CONTINUE: Updating article for final generation');
      await directusAPI.patch(`/items/articles/${articleId}`, updateData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getStoredToken()}`
        }
      });

      log('✅ API CONTINUE: Article updated successfully');

      // Generate full article via API
      log('🚀 API CONTINUE: Generating full article via API');
      await generateArticleViaAPI(articleId, articleData?.thread_id);

      log('✅ API CONTINUE: Full article generated successfully');

      // Fetch the updated article with full content from Directus
      log('🔄 API CONTINUE: Fetching updated article from Directus');
      const completedArticleResponse = await directusAPI.get(`/items/articles/${articleId}`, {
        headers: {
          'Authorization': `Bearer ${getStoredToken()}`
        }
      });
      const completedArticle = completedArticleResponse.data.data;
      log('✅ API CONTINUE: Fetched updated article:', completedArticle.id);
      log('🔍 API CONTINUE: Article draft exists:', !!completedArticle.article_draft);

      // Parse and load the generated FULL article
      let fullArticleContent;
      if (completedArticle.article_draft) {
        try {
          if (typeof completedArticle.article_draft === 'object') {
            fullArticleContent = completedArticle.article_draft;
          } else {
            fullArticleContent = JSON.parse(completedArticle.article_draft);
          }
        } catch (e) {
          log('⚠️ API CONTINUE: Error parsing article content, creating fallback:', e);
          fullArticleContent = parseMarkdownToEditorJS(completedArticle.article_draft || 'Article content generated');
        }
      } else {
        log('⚠️ API CONTINUE: No article content found, keeping existing');
        fullArticleContent = articleData?.article_draft || { time: Date.now(), blocks: [], version: "2.29.1" };
      }

      // Update articleData with new FULL article content
      log('📝 API CONTINUE: Updating articleData with FULL article content');
      const updatedArticleData = {
        ...completedArticle,
        article_draft: fullArticleContent  // This is the key - new FULL article content
      };

      log('📝 API CONTINUE: Full article loaded with', fullArticleContent.blocks?.length || 0, 'blocks');

      // Clear editorRef so useEffect can create new EditorJS with full content
      log('🗑️ API CONTINUE: Clearing editorRef to allow useEffect to refresh with full article');
      if (editorRef.current && editorRef.current.destroy) {
        await editorRef.current.destroy();
      }
      editorRef.current = null;

      // Force useEditor to refresh
      setForceEditorRefresh(prev => !prev);

      // CRITICAL: Use flushSync to force synchronous state updates
      log('⚡ API CONTINUE: Using flushSync to force synchronous state updates');
      flushSync(() => {
        // Update local state - this will trigger useEffect in useEditor.js
        setGeneratedArticleId(completedArticle.id);
        setArticleData(updatedArticleData);  // This triggers useEffect to create EditorJS with FULL content
        setLastSaved(new Date().toISOString());
        setSaveError('');
        setEditorHasContent(true);
        setShowEditor(true);
        setIsDraftGenerated(true);
      });

      log('✅ API CONTINUE: All state updated synchronously using flushSync');
      log('👁️ API CONTINUE: Editor updated with full article content');

      // Set article status back to "completed"
      try {
        log('🔒 API CONTINUE: Setting article status to completed');
        await directusAPI.patch(`/items/articles/${articleId}`, {
          status: 'completed'
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        log('✅ API CONTINUE: Article status set to completed');
      } catch (error) {
        log('❌ API CONTINUE: Error setting completed status:', error);
      }

      // Success screen is already shown at the start of handleContinue()

    } catch (error) {
      log('❌ API CONTINUE ERROR:', error);
      setSaveError('Error generating final article: ' + error.message);
      throw error;
    }
  }

  // Queue-based continue function
  const handleContinueWithQueue = async () => {
    try {
      log('QUEUE CONTINUE: Starting final article generation from draft');

      // ALWAYS fetch latest draft from Directus to ensure we have the most recent changes
      let outputData;
      let articleId = generatedArticleId;

      if (articleId) {
        try {
          log('QUEUE CONTINUE: Fetching latest draft from Directus for article:', articleId);
          const response = await directusAPI.get(`/items/articles/${articleId}`, {
            headers: {
              'Authorization': `Bearer ${getStoredToken()}`
            }
          });

          const latestArticleData = response.data.data;
          if (latestArticleData.article_draft) {
            outputData = typeof latestArticleData.article_draft === 'string'
              ? JSON.parse(latestArticleData.article_draft)
              : latestArticleData.article_draft;
            log('QUEUE CONTINUE: ✅ Using LATEST draft from Directus, blocks count:', outputData.blocks?.length || 0);
            log('QUEUE CONTINUE: ✅ Draft timestamp:', outputData.time);
          }
        } catch (error) {
          log('QUEUE CONTINUE: Error fetching latest draft from Directus:', error);
        }
      }

      // Fallback to editor if Directus fetch failed
      if (!outputData && editorRef.current && editorRef.current.save) {
        try {
          outputData = await editorRef.current.save();
          log('QUEUE CONTINUE: Fallback - using editor data, blocks count:', outputData.blocks.length);
        } catch (error) {
          log('QUEUE CONTINUE: Error saving from editor:', error);
        }
      }

      // Final fallback to old articleData
      if (!outputData && articleData && articleData.article_draft) {
        try {
          outputData = typeof articleData.article_draft === 'string'
            ? JSON.parse(articleData.article_draft)
            : articleData.article_draft;
          log('QUEUE CONTINUE: Final fallback - using old articleData.article_draft, blocks count:', outputData.blocks?.length || 0);
        } catch (error) {
          log('QUEUE CONTINUE: Error parsing articleData.article_draft:', error);
          throw new Error('Cannot get article content. Editor is not available and article data is invalid.');
        }
      }

      if (!outputData || !outputData.blocks || outputData.blocks.length === 0) {
        throw new Error('No article content found. Please ensure the draft has content before generating.');
      }

      log('QUEUE CONTINUE: Using articleId:', articleId);

      if (!articleId) {
        log('QUEUE CONTINUE: No articleId found, something went wrong');
        throw new Error('No article ID found. Please ensure the draft was saved correctly.');
      }

      // Update article with current draft content and set to final generation mode
      log('QUEUE CONTINUE: Updating article for final generation');

      // Use direct axios call with proxy instead of saveArticleToDirectus to avoid CORS
      const updateData = {
        article_draft: JSON.stringify(outputData),
        sources: JSON.stringify(sources),
        keywords: keywords,
        content_type: contentType,
        tone_of_voice: toneOfVoice,
        fog_index: complexity,
        language: language,
        project: selectedProject || null,
        article_linked_structure: selectedStructure || null,
        article_thumbnail_included: includeImages,
        article_thumbnail_source: includeImages ? thumbnailSource : null
        // Removed generation_type - worker will detect continue action by presence of thread_id
      };

      console.log('💾 handleContinueQueue: selectedStructure =', selectedStructure, 'updateData:', updateData);

      log('QUEUE CONTINUE: Update data being sent:', updateData);

      try {
        const response = await directusAPI.patch(`/items/articles/${articleId}`, updateData, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token')}`
          }
        });

        log('QUEUE CONTINUE: Article updated successfully:', response.status);
      } catch (error) {
        log('QUEUE CONTINUE: PATCH request failed');
        log('QUEUE CONTINUE: Error status:', error.response?.status);
        log('QUEUE CONTINUE: Error data:', error.response?.data);
        log('QUEUE CONTINUE: Error details:', JSON.stringify(error.response?.data, null, 2));
        if (error.response?.data?.errors) {
          log('QUEUE CONTINUE: Validation errors:', error.response.data.errors);
          error.response.data.errors.forEach((err, index) => {
            log(`QUEUE CONTINUE: Error ${index + 1}:`, err);
          });
        }
        log('QUEUE CONTINUE: Error message:', error.message);
        log('QUEUE CONTINUE: Request data that failed:', updateData);
        throw error;
      }

      // Add to queue for final processing
      log('QUEUE CONTINUE: Adding article to queue for final generation');
      await queueAPI.addToQueue({ id: articleId });

      // Start polling with UI updates
      log('QUEUE CONTINUE: Starting status polling for final generation');
      const completedArticle = await queueAPI.pollStatus(articleId, (article) => {
        log('QUEUE CONTINUE: Status update:', article.queue_status);
      });

      log('✅ QUEUE CONTINUE: Article generation completed:', completedArticle.id);
      log('🔍 QUEUE CONTINUE: Article draft exists:', !!completedArticle.article_draft);

      // Parse and load the generated FULL article
      let fullArticleContent;
      if (completedArticle.article_draft) {
        try {
          if (typeof completedArticle.article_draft === 'object') {
            fullArticleContent = completedArticle.article_draft;
          } else {
            fullArticleContent = JSON.parse(completedArticle.article_draft);
          }
        } catch (e) {
          log('⚠️ QUEUE CONTINUE: Error parsing article content, creating fallback:', e);
          fullArticleContent = parseMarkdownToEditorJS(completedArticle.article_draft || 'Article content generated');
        }
      } else {
        log('⚠️ QUEUE CONTINUE: No article content found, keeping existing');
        fullArticleContent = articleData?.article_draft || { time: Date.now(), blocks: [], version: "2.29.1" };
      }

      // Update articleData with new FULL article content
      log('📝 QUEUE CONTINUE: Updating articleData with FULL article content');
      const updatedArticleData = {
        ...completedArticle,
        article_draft: fullArticleContent  // This is the key - new FULL article content
      };

      log('📝 QUEUE CONTINUE: Full article loaded with', fullArticleContent.blocks?.length || 0, 'blocks');

      // Clear editorRef so useEffect can create new EditorJS with full content
      log('🗑️ QUEUE CONTINUE: Clearing editorRef to allow useEffect to refresh with full article');
      if (editorRef.current && editorRef.current.destroy) {
        await editorRef.current.destroy();
      }
      editorRef.current = null;

      // Force useEditor to refresh
      setForceEditorRefresh(prev => !prev);

      // CRITICAL: Use flushSync to force synchronous state updates
      log('⚡ QUEUE CONTINUE: Using flushSync to force synchronous state updates');
      flushSync(() => {
        // Update local state - this will trigger useEffect in useEditor.js
        setGeneratedArticleId(completedArticle.id);
        setArticleData(updatedArticleData);  // This triggers useEffect to create EditorJS with FULL content
        setLastSaved(new Date().toISOString());
        setSaveError('');
        setEditorHasContent(true);
        setShowEditor(true);
        setIsDraftGenerated(true);
      });

      log('✅ QUEUE CONTINUE: All state updated synchronously using flushSync');
      log('👁️ QUEUE CONTINUE: Editor updated with full article content');

      // Set article status back to "completed"
      try {
        log('🔒 QUEUE CONTINUE: Setting article status to completed');
        await directusAPI.patch(`/items/articles/${articleId}`, {
          status: 'completed'
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        log('✅ QUEUE CONTINUE: Article status set to completed');
      } catch (error) {
        log('❌ QUEUE CONTINUE: Error setting completed status:', error);
      }

      // Success screen is already shown at the start of handleContinue()
    } catch (error) {
      log('QUEUE CONTINUE: Error in queue-based generation:', error);
      setSaveError('Error generating final article: ' + error.message);
      throw error;
    }
  };

  return {
    handleContinue,
    handleContinueViaAPI,
    handleContinueWithQueue,
  };
}
